import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { usePrevious } from 'hooks';
import Grid from '@material-ui/core/Grid';
import { Formik, Form, Field } from 'formik';

import { BaseModal } from 'components/UI';
import { TextField } from 'components/FormFields';
import { AdHocSelect } from 'components/AsyncFields';
import { CustomersAction } from 'state/modules/customers';
import { parseInputNumber } from 'utils';

const AddCreditsModal = ({ open, account, handleClick, accountLevel, type, to, user }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { errors, fetchedAddCredit, fetchingAddCredit } = useSelector((state) => state.customers);
  const prevFetched = usePrevious(fetchedAddCredit);
  const { core } = useSelector((state) => state.config?.settings || {});
  const hasSubscriptionsEnabled = core?.subscription?.enabled || false;
  const formSchema = Yup.object().shape({
    credit_type_id: Yup.number().required(i18n.t('common.required_field')),
    amount_issued: Yup.number()
      .min(0.01, i18n.t('common.min_value'))
      .required(i18n.t('common.required_field')),
    description: Yup.string(),
  });

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (fetchedAddCredit && fetchedAddCredit !== prevFetched && open) {
      handleClick();
      dispatch(CustomersAction.resetAddCredit());
    }
  }, [fetchedAddCredit, prevFetched, open, handleClick, dispatch]);

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      handleClick={handleClick}
      loading={fetchingAddCredit}
      title={i18n.t('payments.add_credit')}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          credit_type_id: account?.credit_type_id || '',
          amount_issued: account?.amount_issued || 0,
          description: account?.description || '',
        }}
        onSubmit={(values) => {
          const payload = {
            ...values,
            account_id: hasSubscriptionsEnabled && user ? user.account_id : account.id,
          };
          payload.amount_issued = parseInputNumber(payload.amount_issued);
          dispatch(CustomersAction.fetchAddCredit(payload));
        }}
        validationSchema={formSchema}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  to={to}
                  type={type}
                  margin="none"
                  name="credit_type_id"
                  component={AdHocSelect}
                  customParams={{
                    is_revenue: false,
                    is_account_level: accountLevel,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="number"
                  margin="none"
                  format="price"
                  name="amount_issued"
                  inputProperties={{}}
                  component={TextField}
                  asyncErrors={errors?.amount_issued}
                  label={i18n.t('payments.issue_amount')}
                  onFocus={() => {
                    if (parseFloat(values.amount_issued) === 0) {
                      setFieldValue('amount_issued', '');
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="text"
                  margin="none"
                  required={false}
                  name="description"
                  inputProperties={{}}
                  component={TextField}
                  asyncErrors={errors?.description}
                  label={i18n.t('payments.description')}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

AddCreditsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

AddCreditsModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default AddCreditsModal;
